import { useMemo, type ReactNode } from 'react';
import { Activity, Database, GraduationCap, Heart, ListChecks, Sparkles, Trophy, Users } from 'lucide-react';
import { useDashboard, type ProfileCounts } from '../context/DashboardContext';
import { EventCoverBanner } from '../events/EventCoverBanner';
import { ProfileFilter } from '../components/ProfileFilter';
import { DashPanel } from '../components/DashPanel';
import { BarChartHorizontal } from '../components/charts/DashCharts';
import { DonutChart } from '../components/charts/Heatmap';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { exportCardCsv } from '../utils/csvExport';
import { computeRealGeneralAnalytics, topFeatureForProfile } from '../utils/realGeneralAnalytics';
import {
  contentManagementStatsFor,
  topCreatedContentType,
  indexOperatorsByRoomId,
  matchesViewMode,
  buildAnonymousLabels,
  type ContentManagementStats,
} from '../utils/profileFilter';
import { PROFILE_VIEW_MODE_LABELS, type RealProfile, type OperatorProfile } from '../types/profiles';

interface HighlightRow {
  id: string;
  name: string;
  role: string;
  highlightLabel: string;
  highlightText: string;
}

/** Pra cada participante: qual funcionalidade ele mais usou (aluno, ou
 *  professor/pai de conta legada — atividade própria real) OU qual tipo de
 *  conteúdo ele mais criou (professor/pai da versão nova — sem atividade
 *  própria, ver `RealProfile.source`). */
function buildHighlightRows(
  profiles: RealProfile[],
  allProfiles: RealProfile[],
  operatorsByRoomId: Map<string, OperatorProfile>,
  anonymousLabels: Map<string, string>
): HighlightRow[] {
  return profiles.map((p) => {
    const isProfessor = matchesViewMode(p, 'professor', operatorsByRoomId);
    const isPaiMae = matchesViewMode(p, 'pai_mae', operatorsByRoomId);
    const role = isProfessor ? 'Professor(a)' : isPaiMae ? 'Pai/Mãe' : 'Aluno(a)';

    const usaCriacao = (isProfessor || isPaiMae) && p.source === 'new';
    const top = usaCriacao ? topCreatedContentType(contentManagementStatsFor(p, allProfiles)) : topFeatureForProfile(p);

    return {
      id: p.id,
      name: anonymousLabels.get(p.id) ?? p.id,
      role,
      highlightLabel: usaCriacao ? 'Mais criou' : 'Mais usou',
      highlightText: top ? `${top.name} (${top.value.toLocaleString('pt-BR')})` : 'Sem atividade registrada',
    };
  });
}

function RoleCountsRow({ counts }: { counts: ProfileCounts }) {
  const tiles = [
    { icon: <GraduationCap className="h-4 w-4" />, label: 'Aluno(a)', value: counts.managed },
    { icon: <Users className="h-4 w-4" />, label: 'Professor(a)', value: counts.professor },
    { icon: <Heart className="h-4 w-4" />, label: 'Pai/Mãe', value: counts.paiMae },
  ];
  return (
    <div className="grid grid-cols-3 gap-3">
      {tiles.map((t) => (
        <DashPanel key={t.label} padding="sm" className="flex items-center gap-3">
          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary shrink-0">
            {t.icon}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t.label}</p>
            <p className="text-lg font-bold text-foreground tabular-nums">{t.value.toLocaleString('pt-BR')}</p>
          </div>
        </DashPanel>
      ))}
    </div>
  );
}

function ContentStatsPanel({ stats }: { stats: ContentManagementStats }) {
  const tiles = [
    { label: 'Categorias', value: stats.categories },
    { label: 'Subcategorias', value: stats.subcategories },
    { label: 'Rotinas', value: stats.routines },
    { label: 'Subrotinas', value: stats.subRoutines },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {tiles.map((t) => (
        <DashPanel key={t.label} padding="sm">
          <p className="text-xs text-muted-foreground">{t.label}</p>
          <p className="text-xl font-bold text-foreground tabular-nums">{t.value.toLocaleString('pt-BR')}</p>
        </DashPanel>
      ))}
    </div>
  );
}

function StatHero({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: number;
}) {
  return (
    <DashPanel padding="md" className="flex items-center gap-4">
      <div className="flex items-center justify-center h-9 w-9 rounded-full bg-primary/10 text-primary shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-[30px] leading-none font-bold text-foreground tabular-nums">
          {value.toLocaleString('pt-BR')}
        </p>
      </div>
    </DashPanel>
  );
}

function timeAgo(ms: number): string {
  const diffMin = Math.round((Date.now() - ms) / 60_000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.round(diffH / 24);
  return `há ${diffD}d`;
}

export function GeneralModule() {
  const { filteredProfiles, realProfiles, operators, profilesLoading, profileViewMode, activity, profileCounts } =
    useDashboard();

  const analytics = useMemo(() => computeRealGeneralAnalytics(filteredProfiles), [filteredProfiles]);

  // Professor(a)/Pai-Mãe sempre mostra 1 participante por vez (ver
  // filterProfiles em profileFilter.ts) — dá pra saber exatamente qual perfil
  // está selecionado e de qual schema (legado/novo) ele veio.
  const isOperatorView = profileViewMode === 'professor' || profileViewMode === 'pai_mae';
  const selectedOperator = isOperatorView && filteredProfiles.length === 1 ? filteredProfiles[0] : null;
  const contentStats = useMemo(
    () => (selectedOperator ? contentManagementStatsFor(selectedOperator, realProfiles) : null),
    [selectedOperator, realProfiles]
  );
  // Schema novo: professor/pai não tem atividade própria real (currículo vai
  // pro aluno gerenciado, não pro perfil dele) — os gráficos normais ficariam
  // sempre vazios, então a métrica de categorias/rotinas SUBSTITUI, não soma.
  // Schema legado: mantém os gráficos normais (dado real, igual sempre foi) e
  // a métrica de categorias/rotinas entra como extra.
  const showNormalCharts = !selectedOperator || selectedOperator.source === 'legacy';

  // "Ver todos"/"Aluno(a)" — visões com vários participantes juntos, onde faz
  // sentido listar 1 linha por pessoa (Professor(a)/Pai-Mãe já mostra 1 só,
  // em detalhe, nas seções acima).
  const isAggregateView = profileViewMode === 'todos' || profileViewMode === 'managed';
  const operatorsByRoomId = useMemo(() => indexOperatorsByRoomId(operators), [operators]);
  const anonymousLabels = useMemo(() => buildAnonymousLabels(realProfiles), [realProfiles]);
  const highlightRows = useMemo(
    () =>
      isAggregateView ? buildHighlightRows(filteredProfiles, realProfiles, operatorsByRoomId, anonymousLabels) : [],
    [isAggregateView, filteredProfiles, realProfiles, operatorsByRoomId, anonymousLabels]
  );

  return (
    <div>
      <EventCoverBanner />
      <ProfileFilter />

      {!profilesLoading && filteredProfiles.length === 0 && (
        <p className="text-sm text-muted-foreground mb-6">
          Nenhum perfil encontrado para esse filtro no Firestore.
        </p>
      )}

      <section className="mb-6 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatHero
            icon={<Users className="h-[18px] w-[18px]" />}
            label={PROFILE_VIEW_MODE_LABELS[profileViewMode]}
            value={analytics.totalParticipants}
          />
          <StatHero
            icon={<Database className="h-[18px] w-[18px]" />}
            label="Registros totais"
            value={analytics.totalRecords}
          />
          <StatHero
            icon={<Trophy className="h-[18px] w-[18px]" />}
            label="Funcionalidade líder"
            value={analytics.topFeature?.pct ?? 0}
          />
        </div>
        {profileViewMode === 'todos' && <RoleCountsRow counts={profileCounts} />}
      </section>

      {showNormalCharts && (
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">Funcionalidades mais usadas (totais acumulados)</h3>
            {analytics.topFeature && (
              <span className="flex items-center gap-1.5 text-xs font-medium text-primary">
                <Trophy className="h-3.5 w-3.5" />
                {analytics.topFeature.name} lidera com {analytics.topFeature.pct}%
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BarChartHorizontal
              title="Volume de uso por funcionalidade"
              data={analytics.featureUsage.map((f) => ({ name: f.name, value: f.value }))}
              nameKey="name"
              valueKey="value"
              onExport={() =>
                exportCardCsv(
                  'geral-funcionalidades-mais-usadas',
                  ['Funcionalidade', 'Registros', 'Participação (%)'],
                  analytics.featureUsage.map((f) => [f.name, f.value, f.pct])
                )
              }
            />
            <DonutChart
              title="Distribuição por funcionalidade"
              data={analytics.featureUsage.map((f) => ({ label: f.name, value: f.value }))}
              onExport={() =>
                exportCardCsv(
                  'geral-distribuicao-funcionalidade',
                  ['Funcionalidade', 'Registros'],
                  analytics.featureUsage.map((f) => [f.name, f.value])
                )
              }
            />
          </div>
        </section>
      )}

      {contentStats && selectedOperator && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">
              {selectedOperator.source === 'new'
                ? 'Categorias e rotinas sob responsabilidade'
                : 'Categorias e rotinas criadas (adicional)'}
            </h3>
          </div>
          {selectedOperator.source === 'new' && (
            <p className="text-xs text-muted-foreground mb-3">
              Nesta versão do app, professor(a)/pai-mãe não gera atividade própria (jogos, CAA...) — o
              currículo que ele(a) monta fica no aluno que gerencia. Esta contagem soma o currículo de
              todos os alunos sob sua responsabilidade.
            </p>
          )}
          <ContentStatsPanel stats={contentStats} />
        </section>
      )}

      {isAggregateView && highlightRows.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Destaque por participante</h3>
          </div>
          <DashPanel padding="none">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Participante</TableHead>
                  <TableHead>Papel</TableHead>
                  <TableHead>Destaque</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {highlightRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium text-foreground">{row.name}</TableCell>
                    <TableCell className="text-muted-foreground">{row.role}</TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">{row.highlightLabel}: </span>
                      <span className="font-medium text-foreground">{row.highlightText}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DashPanel>
        </section>
      )}

      {profileViewMode === 'managed' && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Atividade recente de gestão</h3>
          </div>
          <DashPanel padding="md">
            {activity.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma atualização de currículo/progresso registrada ainda.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {activity.slice(0, 10).map((a) => (
                  <li key={a.id} className="py-2.5 flex items-center justify-between gap-4 text-sm">
                    <span className="text-foreground">
                      {a.actorNames.length ? a.actorNames.join(', ') : 'Um responsável'}{' '}
                      {a.content === 'curriculum' ? 'atualizou o currículo de' : 'atualizou o progresso de'}{' '}
                      <span className="font-medium">{a.studentName || a.studentId}</span>
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">{timeAgo(a.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </DashPanel>
        </section>
      )}
    </div>
  );
}
